// Per-user preferences: theme (light / dark / auto-by-hour), the whole-app look (uiTheme), colour
// profile, logo, region and date format. localStorage is the instant-apply source of truth (no flash:
// public/theme-boot.js stamps the same attributes before first paint); the server (user_preferences via
// /api/me + /api/preferences) makes the choice follow the account across devices and records that the
// one-time first-run choices have been made.

import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useAuth } from '../auth/AuthContext.js';
import { api } from '../lib/api.js';
import { logoDef, type LogoVariant } from '../lib/logos.js';
import { paletteId, type PaletteId } from '../lib/palettes.js';
import { UI_THEMES, uiThemeId, type UiThemeDef, type UiThemeId } from '../lib/themes.js';

export type ThemePref = 'light' | 'dark' | 'auto';
export type DateFmt = 'locale' | 'DD MMM YYYY' | 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD';

export interface Prefs {
  theme: ThemePref;
  region: string; // BCP-47 locale, e.g. en-GB
  dateFormat: DateFmt;
  themeChosen: boolean; // has the user made the one-time first-run choice?
  cursorHalo: boolean; // show the mouse-follow highlight halo
  lastSeenAnnId: number; // highest announcement id the user has acknowledged (0 = none)
  logo: LogoVariant; // preferred logo colourway ('stock' = the original silver)
  hideHeaders: boolean; // hide the big page titles for a more compact view
  cellHistory: boolean; // the change-history card when the pointer rests on a cell (Overview, Service, GRP, PDI, DS, Deck, HINs)
  hoverTips: boolean; // the small explanation cards when the pointer rests on something (e.g. a Service status)
  palette: PaletteId; // colour profile (chrome + accent) - see lib/palettes
  paletteChosen: boolean; // has the user made the one-time colour-theme choice?
  uiTheme: UiThemeId; // whole-app theme (frame, type, corners, depth) - see lib/themes
}

/** where changes stand with the account: 'saved' once nothing is left waiting; 'local' when the account
 *  didn't respond, so the change is kept here and sent again; 'refused' when the account answered but
 *  turned a value down, so sending it again wouldn't help. */
export type PrefsSync = 'idle' | 'saving' | 'saved' | 'local' | 'refused';

type PrefKey = keyof Prefs;

const LS_KEY = 'rolodex.prefs';
// the keys whose last save hasn't reached the account yet, with the account they belong to
const PENDING_KEY = 'rolodex.prefs.pending';
// while the account isn't answering, try again this often (focus, coming back online and the next change
// also retry straight away)
const RETRY_MS = 30_000;
const DEFAULTS: Prefs = { theme: 'auto', region: 'en-GB', dateFormat: 'locale', themeChosen: false, cursorHalo: true, lastSeenAnnId: 0, logo: 'silver', hideHeaders: false, cellHistory: true, hoverTips: true, palette: 'indigo', paletteChosen: false, uiTheme: 'evolved' };

/** resolve 'auto' to a concrete theme by the local hour: dark 19:00 to 06:59, like Apple devices. */
export function resolveTheme(theme: ThemePref, now: Date = new Date()): 'light' | 'dark' {
  if (theme === 'light' || theme === 'dark') return theme;
  const h = now.getHours();
  return h >= 19 || h < 7 ? 'dark' : 'light';
}

function readLS(): Prefs {
  try {
    return { ...DEFAULTS, ...(JSON.parse(localStorage.getItem(LS_KEY) || '{}') as Partial<Prefs>) };
  } catch {
    return { ...DEFAULTS };
  }
}
function writeLS(p: Prefs) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(p));
  } catch {
    /* ignore quota / privacy mode */
  }
}
interface Pending { email: string; keys: PrefKey[] }
function readPending(): Pending {
  try {
    const p = JSON.parse(localStorage.getItem(PENDING_KEY) || 'null') as Partial<Pending> | null;
    if (p && typeof p.email === 'string' && Array.isArray(p.keys)) {
      return { email: p.email, keys: p.keys.filter((k): k is PrefKey => Object.hasOwn(DEFAULTS, k)) };
    }
  } catch {
    /* malformed: nothing is waiting */
  }
  return { email: '', keys: [] };
}
function writePending(email: string, keys: Set<PrefKey>) {
  try {
    if (keys.size) localStorage.setItem(PENDING_KEY, JSON.stringify({ email, keys: [...keys] }));
    else localStorage.removeItem(PENDING_KEY);
  } catch {
    /* ignore quota / privacy mode */
  }
}
/** the account answered and said no (a bad value). A 401 re-runs sign-in, and 408 / 429 are worth
 *  another try, so those count as not answering. */
function isRefusal(e: unknown): boolean {
  const s = (e as { status?: unknown } | null)?.status;
  return typeof s === 'number' && s >= 400 && s < 500 && s !== 401 && s !== 408 && s !== 429;
}
function applyTheme(resolved: 'light' | 'dark') {
  document.documentElement.setAttribute('data-theme', resolved);
}

interface SettingsValue {
  prefs: Prefs;
  resolvedTheme: 'light' | 'dark';
  /** the active theme's definition (what it turns off, how it draws the mark). */
  uiTheme: UiThemeDef;
  /** where the last change stands with the account (Settings shows it under the title). */
  sync: PrefsSync;
  setTheme: (t: ThemePref) => void;
  setUiTheme: (t: UiThemeId) => void;
  setRegion: (r: string) => void;
  setDateFormat: (d: DateFmt) => void;
  setCursorHalo: (v: boolean) => void;
  setLogo: (v: LogoVariant) => void;
  setHideHeaders: (v: boolean) => void;
  setCellHistory: (v: boolean) => void;
  setHoverTips: (v: boolean) => void;
  setPalette: (v: PaletteId, chosen?: boolean) => void;
  /** true when a signed-in user has not yet made the one-time theme choice (first run). */
  needsThemeChoice: boolean;
  /** the first-run chooser calls this; records the choice and marks it done. */
  chooseTheme: (t: ThemePref) => void;
  /** true when a signed-in user has not yet picked a colour theme (shown after the theme chooser). */
  needsPaletteChoice: boolean;
  /** the first-run colour chooser calls this; records the choice and marks it done. */
  choosePalette: (p: PaletteId) => void;
  /** the first-run brand chooser calls this to set logo + palette together and mark it done. */
  chooseBrand: (logo: LogoVariant, palette: PaletteId) => void;
  /** record that the user has seen announcements up to (and including) this id. */
  markAnnouncementsSeen: (maxId: number) => void;
}

const Ctx = createContext<SettingsValue | null>(null);

export function SettingsProvider({ children }: { children: ReactNode }) {
  const { me } = useAuth();
  const email = me?.user.email ?? '';
  const [prefs, setPrefs] = useState<Prefs>(readLS);
  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>(() => resolveTheme(readLS().theme));
  // the account copy is behind on these keys, so loading it must not overwrite them here, and every
  // save sends them again until one gets through. Kept in localStorage so a reload keeps the promise.
  const [initialPending] = useState(readPending);
  const pending = useRef(new Set<PrefKey>(initialPending.keys));
  const owner = useRef(initialPending.email);
  const [sync, setSync] = useState<PrefsSync>(initialPending.keys.length ? 'local' : 'idle');
  // the values a save sends, current between renders (two saves in one tick must not send a stale value)
  const latest = useRef(prefs);
  // keys the account turned down this session: no longer sent, but a later save mustn't claim 'saved'
  // while one is outstanding. Cleared when that key is saved again successfully.
  const refused = useRef(new Set<PrefKey>());
  const seq = useRef(0);
  const inflight = useRef(0);
  const retryTimer = useRef<number | undefined>(undefined);

  // persist + apply on every change
  useEffect(() => {
    latest.current = prefs;
    writeLS(prefs);
    const r = resolveTheme(prefs.theme);
    setResolvedTheme(r);
    applyTheme(r);
  }, [prefs]);

  // keep the browser tab icon in sync with the chosen logo colourway.
  useEffect(() => {
    const href = logoDef(prefs.logo).src;
    document.querySelectorAll('link[rel="icon"], link[rel="apple-touch-icon"]').forEach((l) => l.setAttribute('href', href));
  }, [prefs.logo]);

  // toggle a root flag so CSS can hide the big page titles for a compact view.
  useEffect(() => {
    document.documentElement.toggleAttribute('data-hide-headers', prefs.hideHeaders);
  }, [prefs.hideHeaders]);

  // apply the chosen colour profile so the [data-palette] token overrides take effect.
  useEffect(() => {
    document.documentElement.setAttribute('data-palette', paletteId(prefs.palette));
  }, [prefs.palette]);

  // apply the whole-app theme so the [data-ui-theme] token blocks and themes.css take effect.
  useEffect(() => {
    document.documentElement.setAttribute('data-ui-theme', uiThemeId(prefs.uiTheme));
  }, [prefs.uiTheme]);

  // auto mode: re-resolve on an interval + on window focus so it flips at dusk/dawn with no reload
  useEffect(() => {
    if (prefs.theme !== 'auto') return;
    const tick = () => {
      const r = resolveTheme(prefs.theme);
      setResolvedTheme(r);
      applyTheme(r);
    };
    const id = window.setInterval(tick, 60_000);
    window.addEventListener('focus', tick);
    return () => {
      window.clearInterval(id);
      window.removeEventListener('focus', tick);
    };
  }, [prefs.theme]);

  // send the keys that are waiting, each at its current value. The server merges a patch into the stored
  // blob, so this is the changed keys plus any an earlier save didn't get through. Only the newest request
  // sets the status, so an older reply can't overwrite a newer one. No refetch of /api/me: the local
  // prefs are authoritative for this session.
  const flush = useCallback(() => {
    window.clearTimeout(retryTimer.current);
    if (!pending.current.size) return;
    const body: Partial<Record<PrefKey, unknown>> = {};
    for (const k of pending.current) body[k] = latest.current[k];
    // a key stops waiting once the account has answered for its current value; one changed again since
    // is already on its way in a newer request
    const settle = () => {
      const done = (Object.keys(body) as PrefKey[]).filter((k) => Object.is(latest.current[k], body[k]));
      for (const k of done) pending.current.delete(k);
      writePending(owner.current, pending.current);
      return done;
    };
    const n = ++seq.current;
    inflight.current += 1;
    setSync('saving');
    api
      .post('/api/preferences', body)
      .then(() => {
        for (const k of settle()) refused.current.delete(k);
        // 'saved' only when nothing this session is still missing from the account
        if (n === seq.current) setSync(pending.current.size ? 'local' : refused.current.size ? 'refused' : 'saved');
      })
      .catch((e: unknown) => {
        // a value the account turns down would fail every later save too, so it stops being sent and
        // stays on this device only. Anything else (offline, a 5xx) waits and goes again.
        const refusal = isRefusal(e);
        if (refusal) for (const k of settle()) refused.current.add(k);
        if (n !== seq.current) return;
        setSync(refusal ? 'refused' : 'local');
        if (!refusal) retryTimer.current = window.setTimeout(() => flush(), RETRY_MS);
      })
      .finally(() => { inflight.current -= 1; });
  }, []);

  // once the account is known, drop anything a different account left waiting on this device (it isn't
  // this person's choice to send), then send what's still waiting. Runs before the adopt effect below,
  // so that one never protects the other account's keys.
  useEffect(() => {
    if (!email) return;
    if (owner.current && owner.current !== email) {
      pending.current.clear();
      refused.current.clear();
      setSync('idle');
    }
    owner.current = email;
    writePending(email, pending.current);
    flush();
  }, [email, flush]);

  // try again as soon as there's a reason to think the account is back
  useEffect(() => {
    const retry = () => { if (!inflight.current) flush(); };
    window.addEventListener('online', retry);
    window.addEventListener('focus', retry);
    return () => {
      window.removeEventListener('online', retry);
      window.removeEventListener('focus', retry);
      window.clearTimeout(retryTimer.current);
    };
  }, [flush]);

  // adopt server prefs once `me` loads (and when a refetch brings a newer copy), so the choice follows the
  // account across devices. A key still waiting to reach the account keeps this device's value: the
  // account copy is the older one.
  const serverStr = me?.user?.preferences ? JSON.stringify(me.user.preferences) : '';
  useEffect(() => {
    if (!serverStr) return;
    try {
      const sp = JSON.parse(serverStr) as Partial<Prefs>;
      setPrefs((prev) => {
        const take = <K extends PrefKey>(k: K): Prefs[K] => (pending.current.has(k) ? prev[k] : sp[k] ?? prev[k]);
        return {
          theme: take('theme'),
          region: take('region'),
          dateFormat: take('dateFormat'),
          themeChosen: take('themeChosen'),
          cursorHalo: take('cursorHalo'),
          lastSeenAnnId: take('lastSeenAnnId'),
          logo: take('logo'),
          hideHeaders: take('hideHeaders'),
          cellHistory: take('cellHistory'),
          hoverTips: take('hoverTips'),
          palette: take('palette'),
          paletteChosen: take('paletteChosen'),
          uiTheme: take('uiTheme'),
        };
      });
    } catch {
      /* ignore malformed */
    }
  }, [serverStr]);

  // apply a change at once (state + localStorage), mark its keys as waiting, then send them
  const save = useCallback((patch: Partial<Prefs>) => {
    latest.current = { ...latest.current, ...patch };
    setPrefs((prev) => ({ ...prev, ...patch }));
    for (const k of Object.keys(patch) as PrefKey[]) pending.current.add(k);
    writePending(owner.current, pending.current);
    flush();
  }, [flush]);

  const value: SettingsValue = {
    prefs,
    resolvedTheme,
    uiTheme: UI_THEMES[uiThemeId(prefs.uiTheme)],
    sync,
    setTheme: (t) => save({ theme: t, themeChosen: true }),
    setUiTheme: (t) => save({ uiTheme: t }),
    setRegion: (r) => save({ region: r }),
    setDateFormat: (d) => save({ dateFormat: d }),
    setCursorHalo: (v) => save({ cursorHalo: v }),
    setLogo: (v) => save({ logo: v }),
    setHideHeaders: (v) => save({ hideHeaders: v }),
    setCellHistory: (v) => save({ cellHistory: v }),
    setHoverTips: (v) => save({ hoverTips: v }),
    // the first-run chooser previews live with chosen=false, so it doesn't count as the one-time choice
    setPalette: (v, chosen = true) => save(chosen ? { palette: v, paletteChosen: true } : { palette: v }),
    // show the one-time chooser only for a signed-in, approved user who has NOT chosen — checking
    // the server flag directly too, so an already-chosen account never flashes it on a new device
    // (before the localStorage sync catches up).
    needsThemeChoice:
      !!me && !me.user.pending && !prefs.themeChosen && !me.user.preferences?.themeChosen,
    chooseTheme: (t) => save({ theme: t, themeChosen: true }),
    // shown once the theme choice is settled, so the two first-run popups don't stack.
    needsPaletteChoice:
      !!me && !me.user.pending &&
      (prefs.themeChosen || !!me.user.preferences?.themeChosen) &&
      !prefs.paletteChosen && !me.user.preferences?.paletteChosen,
    choosePalette: (p) => save({ palette: p, paletteChosen: true }),
    chooseBrand: (logo, palette) => save({ logo, palette, paletteChosen: true }),
    markAnnouncementsSeen: (maxId) => save({ lastSeenAnnId: Math.max(prefs.lastSeenAnnId, maxId) }),
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** the preferences, or the defaults outside a <SettingsProvider> (for small shared pieces such as a hover
 *  card, which render in tests without one). */
export function usePrefs(): Prefs {
  return useContext(Ctx)?.prefs ?? DEFAULTS;
}

export function useSettings(): SettingsValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useSettings must be used within <SettingsProvider>');
  return v;
}
